/**
 * <soulog-spec-video> -- scroll-progress-driven frame-by-frame video playback (sections/soulog-spec-video.liquid)
 *
 * Reuses scroll-video-core.js: blobLoad downloads the whole file before seeking (dragging a streamed src stutters),
 * createSmoothSeeker does time smoothing + frame-grid quantization (it also smooths Windows mouse-wheel stepped scrolling into continuous motion).
 * The custom element's connected/disconnected naturally cover the theme editor's section load / unload.
 *
 * Pinned playback: when the video frame scrolls to just below the header and is fully on screen, the stage pins (sticky), plays through frame by frame during the pinned travel,
 * and unpins at the same instant the last frame appears. **Every frame fully on screen** is the sole purpose of this structure (user request, 2026-09-14).
 *
 * Why not "no pinning, play while scrolling" (tried and reverted the same day): the video starts playing as soon as it peeks in, while its lower half is still below the bottom of the screen;
 * of the 141 frames with content, 132 were cut off; and the scroll window where the frame is fully on screen is only 191px at 1920×1080, and 0 on a MacBook.
 * The cost of pinning is that the page doesn't move during this scroll (trackpad inertia gets swallowed; tested on a real device in Firefox, it isn't dropped frames);
 * mitigation comes from making the footage's motion pacing even, not from reverting to no pinning or adding a hold after the last frame.
 *
 * Fallback image (2026-09-16): the 3.56MB video only shows anything once fully downloaded, so on slow networks / CDN failures this section would be blank --
 * and it's the most labor-intensive effect on the whole page, so "never mind if they don't see it" is not acceptable. A static last-frame image is an independent <img>, absolutely positioned over the video:
 * with no JS / reduced motion you see it directly; once JS takes over it's hidden (the video starts with pure black, so normally the last frame must not flash first);
 * if after FALLBACK_DELAY of downloading there's still no drawable frame, it's shown, and fades out when the video arrives later.
 * The structure mirrors apple.com/airpods-pro's startframe (0 posters on that whole page); the 3 seconds comes from its data-load-timeout.
 * ⚠️ The video poster attribute isn't used: poster only shows when there's no src; once src is attached the video takes over,
 * so it can't express the intermediate state "already downloading, but no picture yet, show an image meanwhile".
 *
 * Tail slack (2026-09-17, "the last few frames aren't fully visible"): only affects the **unpinned** tier. This tier's entire playback travel
 * is the scroll during which "the frame is fully on screen", = visible area height - frame height, ~412px on a 1920 screen and ~300px on a MacBook;
 * while the seeker's time smoothing is a fixed 140ms, and one trackpad swipe (2000~4000px/s) scrolls the whole stretch in 0.1s,
 * so the last batch of frames is drawn only after the frame has already slid behind the header -- which looks like "the last few frames weren't seen".
 * Three things fix it together; none can be left out:
 *   1. FREE_TAIL: the video finishes early at 80% of the travel; during the remaining 20% the frame is still fully on screen, resting on the last frame;
 *   2. FREE_SMOOTH_MS: this tier's travel is short, so the smoothing constant drops from 140ms to 80ms to keep up;
 *   3. snap when progress is full: write the last frame directly without waiting for smoothing to converge -- when scrolling fast neither 1 nor 2 is quick enough; this one is the fallback.
 * ⚠️ The pinned tier is entirely unchanged (80vh of travel is long enough, and the 140ms smoothing exists precisely to fill in Windows mouse-wheel stepped scrolling).
 * ⚠️ This is not "adding a hold after the last frame": during the tail the page keeps scrolling with the finger as usual, and trackpad inertia isn't swallowed.
 *
 * Playback travel multiplier (2026-09-17, "it plays too fast now, could we multiply by a factor"): also only affects the **unpinned** tier.
 * The travel above (visible area - frame height) is only ~400px on a 1920 screen and ~300px on a MacBook; spreading 283 frames across it
 * means one frame per 1px scrolled -- one trackpad swipe plays the whole thing in 0.15s. The admin "playback travel multiplier" (--ssv-free-span)
 * expands the playback range equally toward both ends, centered on the "frame fully on screen" stretch: with multiplier k, travel = range × k,
 * and the start moves earlier by range × (k-1) / 2. The cost is that the first few frames play before the frame is fully on screen, and the last few after it starts leaving
 * (both ends are black / near-still segments, so the impact is small) -- if you don't want to miss a single frame, only the pinned tier will do.
 * ⚠️ The tail slack keeps its original **absolute** length (range × FREE_TAIL) rather than scaling with k: it compensates the fixed latency of seek + decode,
 * which isn't proportional; with k = 1 it's exactly equivalent to before the change.
 * ⚠️ The pinned tier ignores this factor -- to slow that one down, adjust the "fixed playback travel" (--ssv-pin) directly.
 *
 * Soft start (2026-09-15): "a hitch right when the page starts moving after unpinning" (trackpad) -- the page's visual speed jumps instantly from 0 to
 * the scroll speed. After the last frame, a --ssv-ramp acceleration segment is added where the stage speed rises linearly from 0 to the scroll speed (rampLift),
 * so the page is moving from the very first px; it's not a hold. The video still finishes within the pinned travel, so seeing every frame in full is unaffected.
 */
if (!customElements.get("soulog-spec-video")) {
	// Matches the footage frame rate (60fps). Quantizing at 1/30 would swallow half the frames of 60fps footage, making 60fps pointless
	const FRAME_STEP = 1 / 60;
	/*
	 * No pinning in portrait (2026-09-15, "too much empty space above the video on mobile"): in portrait a 16:9 frame centered in a full-screen stage leaves ~330px empty above and below,
	 * while the frame is only screen width × 9/16 tall, so even unpinned there's ~660px (iPhone) of scrolling with the frame fully on screen, enough to play through frame by frame.
	 * Must match exactly the media query in soulog-spec-video.css that cancels pinning.
	 * Exception: when the host has data-pin-portrait it pins in portrait too (the home page's 11-second clip; unpinned it has only ~500px of travel, too fast; 2026-09-26).
	 */
	const FREE_QUERY = "(max-width: 1366px) and (orientation: portrait)";
	/* Time given to the download before showing the fallback image. Aligned with apple.com/airpods-pro's data-load-timeout="3000" */
	const FALLBACK_DELAY = 3000;
	/* If there's still no drawable frame by this point, assume the video isn't coming: the fallback image stays, and the pinned travel is collapsed if the user hasn't scrolled there yet */
	const HARD_TIMEOUT = 20000;
	/* Added on the host: the fallback image gives way to the video (in CSS it's .ssv--ready .ssv__fallback { opacity: 0 }) */
	const READY_CLASS = "ssv--ready";
	/* Added on the host: cancel pinning, collapse the travel (shares the same set of CSS variables with portrait / reduced motion) */
	const UNPINNED_CLASS = "ssv--unpinned";
	/*
	 * Tail slack for the unpinned tier (as a fraction of the "frame fully on screen" travel): the video finishes before this point,
	 * and during the remaining scroll the frame is still fully on screen, resting on the last frame, leaving time for seek + decode to catch up.
	 * The cost is that the effective playback travel is shorter by the same fraction (plays a bit faster) -- in exchange the last frame is always seen in full. See the file header.
	 */
	const FREE_TAIL = 0.2;
	/* Smoothing constant for the unpinned tier: travel is only 300~400px, and a 140ms tail would be flung outside the frame (item 2 in the file header) */
	const FREE_SMOOTH_MS = 80;
	/* The pinned tier keeps core's default -- 80vh of travel, and the long tail nicely fills in Windows mouse-wheel stepped scrolling */
	const PIN_SMOOTH_MS = 140;
	/* Upper limit for the playback travel multiplier: the admin range max is 300%; the code clamps once more to block absurd values from hand-edited templates */
	const FREE_SPAN_MAX = 4;
	/*
	 * Idle stop (2026-10-09): the loop used to run every frame for as long as the section was on screen -- two getComputedStyle
	 * calls and a getBoundingClientRect per frame even while the user just sits there reading, which on phones is pure heat and battery.
	 * Now every scroll / resize / layout change / video metadata arrival "wakes" the loop, and it stops IDLE_MS after the last wake.
	 * 1000ms covers the slowest glide: worst case is a full-clip jump (~4.7s of video) with the 140ms pinned constant,
	 * which needs 140 x ln(4.7 x 60) ~ 790ms to fall inside the one-frame snap window.
	 */
	const IDLE_MS = 1000;

	class SoulogSpecVideo extends HTMLElement {
		/**
		 * Scroll progress (0~1).
		 * stickyTop: the stage's sticky top (= header height); trackTop: the track's getBoundingClientRect().top;
		 * range: track height - stage height = pinned travel.
		 * Start trackTop = stickyTop (just pinned); end trackTop = stickyTop - range (unpinned).
		 *
		 * lead (px, optional): start this far **before** pinning (2026-09-28, home soulog-home-everyday: the clip opens on ~1s of pure black,
		 * so at the moment of pinning the frame was black and you had to scroll ~30vh more to see the product). The black head is consumed
		 * by the approach scroll; the end is unchanged (last frame exactly at unpinning). 0 / omitted / invalid = old behavior.
		 */
		static progressAt(stickyTop, trackTop, range, lead) {
			if (!(range > 0)) return 0;
			const l = lead > 0 ? lead : 0;
			return Math.min(1, Math.max(0, (stickyTop + l - trackTop) / (range + l)));
		}

		/**
		 * Scroll progress when unpinned (0~1). Shared by three cases: portrait, "fixed playback" turned off in the admin, and load failure.
		 * mediaTop / mediaHeight: the video frame's getBoundingClientRect; viewTop: header bottom edge; viewBottom: viewport bottom edge.
		 * Start: the frame's bottom edge has just fully appeared above the viewport bottom; end: the frame's top edge touches the header bottom -- every frame in between is fully on screen.
		 *
		 * tailRatio: tail slack fraction (see "Tail slack" in the file header). Progress reaches 1 after (1 - tailRatio) of the travel,
		 * and during the remaining scroll the frame is still fully on screen, resting on the last frame. 0 = old behavior (fills the whole stretch).
		 *
		 * spanRatio: playback travel multiplier (see "Playback travel multiplier" in the file header). When k > 1, travel = range × k,
		 * expanded equally toward both ends, centered on the "frame fully on screen" stretch (start moves earlier by range × (k-1) / 2);
		 * the tail slack is still deducted as the absolute length range × tailRatio, not scaled by k. 1 / omitted = old behavior.
		 */
		static progressInView(mediaTop, mediaHeight, viewTop, viewBottom, tailRatio, spanRatio) {
			const range = viewBottom - viewTop - mediaHeight;
			if (!(range > 0)) return 0;
			const tail = Math.min(0.9, Math.max(0, tailRatio || 0));
			const span = Math.min(FREE_SPAN_MAX, Math.max(1, spanRatio || 1));
			const played = range * (span - tail);
			if (!(played > 0)) return 0;
			// How much earlier the start is: 0 when k = 1, exactly equivalent to before the change
			const lead = (range * (span - 1)) / 2;
			return Math.min(1, Math.max(0, (viewBottom - mediaTop - mediaHeight + lead) / played));
		}

		/**
		 * Soft start: extra upward offset (px) of the stage above its native sticky position after the last frame.
		 * u: distance scrolled past the video's last frame; ramp: length of the acceleration segment.
		 * Target visual displacement D(u) = u² / (2·ramp), speed u / ramp rising linearly from 0 to 1; at u = ramp, D = ramp / 2,
		 * joining up with native scrolling (speed 1). CSS adds ramp / 2 to the track, so native sticky only unpins at u = ramp / 2,
		 * therefore the offset = D(u) - max(0, u - ramp / 2): ≥ 0 throughout, peaking at ramp / 8, and 0 outside the acceleration segment.
		 */
		static rampLift(u, ramp) {
			if (!(ramp > 0) || !(u > 0) || u >= ramp) return 0;
			const d = u <= ramp / 2 ? u : ramp - u;
			return (d * d) / (2 * ramp);
		}

		// Scroll progress p (0~1) → video time. The last frame is duration - one frame; seeking to duration itself turns the screen black in some browsers
		static timeAt(progress, duration) {
			if (!(duration > 0)) return 0;
			const p = Math.min(1, Math.max(0, progress));
			return p * Math.max(0, duration - FRAME_STEP);
		}

		connectedCallback() {
			const core = window.ScrollVideoCore;
			this.video = this.querySelector(".ssv__video");
			this.fallback = this.querySelector(".ssv__fallback");
			this.media = this.querySelector(".ssv__media");
			this.track = this.querySelector(".ssv__track");
			this.stage = this.querySelector(".ssv__stage");
			// Outer wrapper for portrait pinning (only the home page soulog-home-everyday has it): title + video + description pin together as a block, see pinTargets()
			this.ptrack = this.querySelector(".ssv__ptrack");
			this.pstage = this.querySelector(".ssv__pstage");
			if (!core || !this.video || !this.media || !this.track || !this.stage) return;
			// Reduced motion: keep the fallback image (don't add READY_CLASS); CSS has already cancelled pinning; don't download the video
			if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

			this.core = core;
			this.destroyed = false;
			this.lift = 0;
			this.freeQuery = window.matchMedia(FREE_QUERY);
			this.video.muted = true;
			/*
			 * Optimistically hide the fallback image first: on a normal network the video is ready quickly, and showing the last frame first would spoil the ending.
			 * While it's hidden the picture is pure black -- exactly what the video's first frame looks like, so the transition isn't jarring.
			 * If it's genuinely slow, FALLBACK_DELAY brings it back.
			 *
			 * Exception: the fallback image is the first frame (home page soulog-home-everyday, host has data-fallback="start").
			 * The picture matches the start of the video, so there's no need to hide it -- it stays up until the video can draw (loadeddata → markReady) and then fades out, so the frame isn't empty while downloading.
			 */
			if (this.dataset.fallback !== "start") this.classList.add(READY_CLASS);
			this.onLoadedData = () => this.markReady();
			this.onVideoError = () => this.giveUp();
			this.video.addEventListener("loadeddata", this.onLoadedData);
			this.video.addEventListener("error", this.onVideoError);
			this.seeker = core.createSmoothSeeker(this.video, { frameStep: FRAME_STEP });
			this.removeUnlock = core.installTouchUnlock(this.video, false);
			this.tick = this.tick.bind(this);

			// Anything that can move the target frame wakes the loop (see IDLE_MS)
			this.lastWake = 0;
			this.wake = () => {
				this.lastWake = performance.now();
				if (this.inView) this.start();
			};
			// Back from a background tab: rAF was paused there, recompute once
			this.onVisibility = () => {
				if (document.visibilityState === "visible") this.wake();
			};
			window.addEventListener("scroll", this.wake, { passive: true });
			window.addEventListener("resize", this.wake);
			document.addEventListener("visibilitychange", this.onVisibility);
			// The video becoming seekable moves nothing on screen, but the frame for the current position has to be written now
			this.video.addEventListener("loadedmetadata", this.wake);
			// Layout shifting above (images / fonts loading) moves the track without a scroll event
			if (typeof ResizeObserver === "function") {
				this.layoutObserver = new ResizeObserver(this.wake);
				this.layoutObserver.observe(this);
				if (document.body) this.layoutObserver.observe(document.body);
			}

			// Start downloading two screens ahead, so the whole file is in hand by the time the user scrolls there
			this.loadObserver = new IntersectionObserver(
				(entries) => {
					if (!entries.some((e) => e.isIntersecting)) return;
					this.loadObserver.disconnect();
					this.load();
				},
				{ rootMargin: "200% 0px" },
			);
			this.loadObserver.observe(this);

			// Only run rAF while the section is visible
			this.viewObserver = new IntersectionObserver((entries) => {
				this.inView = entries[entries.length - 1].isIntersecting;
				// Entering view jumps straight to the current frame (no glide from wherever it was left); idle restarts keep gliding
				if (this.inView) {
					this.seeker.reset();
					this.wake();
				}
			});
			this.viewObserver.observe(this);
		}

		disconnectedCallback() {
			this.destroyed = true;
			this.clearTimers();
			if (this.wake) {
				window.removeEventListener("scroll", this.wake);
				window.removeEventListener("resize", this.wake);
				document.removeEventListener("visibilitychange", this.onVisibility);
				if (this.video) this.video.removeEventListener("loadedmetadata", this.wake);
			}
			if (this.layoutObserver) this.layoutObserver.disconnect();
			if (this.video) {
				if (this.onLoadedData) this.video.removeEventListener("loadeddata", this.onLoadedData);
				if (this.onVideoError) this.video.removeEventListener("error", this.onVideoError);
			}
			if (this.rafId) cancelAnimationFrame(this.rafId);
			this.rafId = 0;
			if (this.liftEl) this.liftEl.style.transform = "";
			this.liftEl = null;
			this.lift = 0;
			if (this.loadObserver) this.loadObserver.disconnect();
			if (this.viewObserver) this.viewObserver.disconnect();
			if (this.removeUnlock) this.removeUnlock();
			if (this.objectUrl) URL.revokeObjectURL(this.objectUrl);
			this.objectUrl = null;
		}

		/*
		 * All three state transitions of the fallback image live here:
		 *   showFallback  download too slow → show the fallback image (the video is still downloading and will cover it when it arrives)
		 *   markReady     a frame can be drawn → fade out the fallback image; the video takes over from then on
		 *   giveUp        can't get it at all → the fallback image stays; the pinned travel is also collapsed only if the user hasn't scrolled there yet
		 */
		clearTimers() {
			if (this.softTimer) clearTimeout(this.softTimer);
			if (this.hardTimer) clearTimeout(this.hardTimer);
			this.softTimer = 0;
			this.hardTimer = 0;
		}

		showFallback() {
			if (this.destroyed) return;
			this.classList.remove(READY_CLASS);
		}

		markReady() {
			if (this.destroyed) return;
			this.clearTimers();
			this.classList.add(READY_CLASS);
		}

		giveUp() {
			if (this.destroyed) return;
			this.clearTimers();
			this.showFallback();
			/*
			 * Only collapse the travel if the user hasn't seen this section yet. Pulling out 1.8 screens of travel once they've scrolled into the pinned stretch
			 * would make the page jump on the spot -- worse than making someone scroll one more screen past a still image.
			 */
			if (!this.inView) this.classList.add(UNPINNED_CLASS);
		}

		/*
		 * Three cases take the unpinned branch: portrait (media query), "fixed playback" turned off in the admin (Liquid adds a class),
		 * and the video being completely unobtainable (JS adds the same class). CSS expresses these with the same set of variables, and JS here must share the same source --
		 * otherwise CSS has already collapsed the track to auto while JS is still computing progress from the pinned travel, and progress would be stuck at 0 or 1.
		 */
		isFree() {
			if (this.classList.contains(UNPINNED_CLASS)) return true;
			// Portrait pinning (home page soulog-home-everyday has data-pin-portrait + outer .ssv__ptrack / .ssv__pstage, see pinTargets)
			return this.freeQuery.matches && !(this.pstage && this.ptrack && this.hasAttribute("data-pin-portrait"));
		}

		load() {
			const src = this.video.dataset.src;
			if (!src) return;
			this.softTimer = setTimeout(() => this.showFallback(), FALLBACK_DELAY);
			this.hardTimer = setTimeout(() => {
				if (!(this.video.readyState >= 2)) this.giveUp();
			}, HARD_TIMEOUT);
			this.core
				.blobLoad(src)
				.then((url) => {
					if (this.destroyed) {
						URL.revokeObjectURL(url);
						return;
					}
					this.objectUrl = url;
					this.attach(url);
				})
				.catch(() => {
					// If the whole file can't be fetched fall back to streaming; if that fails too it triggers the video's error → giveUp()
					if (!this.destroyed) this.attach(src);
				});
		}

		attach(src) {
			this.video.preload = "auto";
			this.video.src = src;
			this.video.load();
			this.seeker.invalidate();
		}

		/*
		 * Which track / stage pair the pinned tier uses. Default is the video's own .ssv__track / .ssv__stage;
		 * in portrait with data-pin-portrait on the host it switches to the outer .ssv__ptrack / .ssv__pstage -- title, video and description pin together as a block;
		 * pinning only the video leaves a 16:9 strip in portrait with ~290px of black above and below (2026-09-26 option B; user: "so ugly, tons of empty space").
		 * On desktop the two outer elements are display: contents and don't participate in layout. CSS uses the same condition (soulog-spec-video.css).
		 */
		pinTargets() {
			if (this.ptrack && this.pstage && this.freeQuery.matches && this.hasAttribute("data-pin-portrait")) {
				return [this.ptrack, this.pstage];
			}
			return [this.track, this.stage];
		}

		// Only write when the value changes; clear the transform outside the acceleration segment so it isn't a permanent compositing layer. When rotation changes the stage, clear the old one first
		setLift(lift, el = this.stage) {
			if (el !== this.liftEl) {
				if (this.liftEl) this.liftEl.style.transform = "";
				this.liftEl = el;
				this.lift = 0;
			}
			const px = Math.round(lift * 10) / 10;
			if (px === this.lift) return;
			this.lift = px;
			el.style.transform = px > 0 ? `translate3d(0, ${-px}px, 0)` : "";
		}

		start() {
			if (this.rafId || this.destroyed) return;
			this.lastTs = performance.now();
			this.rafId = requestAnimationFrame(this.tick);
		}

		tick(now) {
			this.rafId = 0;
			if (this.destroyed) return;
			const dt = Math.min(now - this.lastTs, 100);
			this.lastTs = now;

			// Header height changes with breakpoints / the announcement bar, so read it fresh every frame
			const stageStyle = getComputedStyle(this.stage);
			// Rotation / load failure can switch tiers at runtime, so decide every frame; the smoothing constant switches accordingly (see "Tail slack" in the file header)
			const free = this.isFree();
			this.seeker.setSmoothMs(free ? FREE_SMOOTH_MS : PIN_SMOOTH_MS);
			let progress;
			if (free) {
				// Unpinned: CSS has already cancelled pinning; play through during the scroll where the frame is fully visible between the header bottom and viewport bottom; no soft start
				this.setLift(0);
				const headerH = parseFloat(stageStyle.getPropertyValue("--header-height")) || 0;
				// The admin "playback travel multiplier", written by Liquid inline on the host and inherited by the stage; read fresh every frame so theme editor changes take effect immediately
				const span = parseFloat(stageStyle.getPropertyValue("--ssv-free-span")) || 1;
				const rect = this.media.getBoundingClientRect();
				progress = SoulogSpecVideo.progressInView(rect.top, rect.height, headerH, window.innerHeight, FREE_TAIL, span);
			} else {
				const [track, stage] = this.pinTargets();
				if (stage === this.pstage) {
					// Outer stage height = title + video + description; CSS uses it to compute the vertically centered sticky top and the travel (--ssv-pin-h)
					const h = stage.offsetHeight + "px";
					if (this.style.getPropertyValue("--ssv-pin-h") !== h) this.style.setProperty("--ssv-pin-h", h);
				}
				// sticky's top comes from --header-height (for the outer stage it's the centered value)
				const stickyTop = parseFloat(getComputedStyle(stage).top) || 0;
				const ramp = parseFloat(stageStyle.getPropertyValue("--ssv-ramp")) || 0;
				const trackTop = track.getBoundingClientRect().top;
				// Video travel = native pinned travel - first half of the soft start (the ramp / 2 that CSS adds to the track)
				const videoRange = track.offsetHeight - stage.offsetHeight - ramp / 2;
				this.setLift(SoulogSpecVideo.rampLift(stickyTop - trackTop - videoRange, ramp), stage);
				// Lead-in before pinning, in vh (written by Liquid inline on the host; only the home section sets it)
				const lead = ((parseFloat(stageStyle.getPropertyValue("--ssv-lead")) || 0) * window.innerHeight) / 100;
				progress = SoulogSpecVideo.progressAt(stickyTop, trackTop, videoRange, lead);
			}

			const video = this.video;
			if (video.readyState >= 1 && video.duration > 0) {
				/*
				 * When progress is full in the unpinned tier, snap straight to the last frame without waiting for smoothing to converge: the tail slack is only a few dozen px,
				 * and when scrolling fast even the 80ms tail can't finish, flinging the last frame outside the frame (item 3 of "Tail slack" in the file header).
				 * The pinned tier doesn't snap -- there unpinning and the last frame coincide anyway, so smoothing runs to completion.
				 */
				this.seeker.update(SoulogSpecVideo.timeAt(progress, video.duration), dt, free && progress >= 1);
			}

			// Keep going only while something recently moved; the next scroll / resize restarts it (see IDLE_MS)
			if (this.inView && now - this.lastWake < IDLE_MS) this.rafId = requestAnimationFrame(this.tick);
		}
	}

	customElements.define("soulog-spec-video", SoulogSpecVideo);
}
